import React from 'react';
import './ProductGrid.css';
import { 
  MdSecurity, 
  MdAccessTime, 
  MdLocationCity, 
  MdDataUsage, 
  MdElevator, 
  MdFingerprint, 
  MdPerson, 
  MdRoomService,
  MdFace
} from 'react-icons/md';
import { 
  FaCheck, 
  FaArrowRight, 
  FaLock, 
  FaBuilding, 
  FaQrcode, 
  FaExchangeAlt 
} from 'react-icons/fa';

const solutionsData = [
  {
    title: "Access Control",
    description: "Enhance high-security, sensitive facilities with comprehensive, fool-proof digital credential systems.",
    icon: <MdSecurity />
  },
  {
    title: "Time-Attendance Solutions",
    description: "Centralized attendance policy tracking across multi-city branches with automated HR syncing.",
    icon: <MdAccessTime />
  },
  {
    title: "SOHO & SMB Lockpacks",
    description: "Affordable and compact biometric kits engineered for single office entries and startup environments.",
    icon: <MdLocationCity />
  },
  {
    title: "Data Center Vaults",
    description: "Empower high-security server rooms with multi-factor card, finger, and biometric cabinets.",
    icon: <MdDataUsage />
  },
  {
    title: "Elevator Access Controls",
    description: "Regulate elevator lift mechanisms to permit floor-by-floor permissions based on department credentials.",
    icon: <MdElevator />
  },
  {
    title: "Aadhar Biometric Attendance",
    description: "Integrated AEBAS terminals compliant with government payroll portals and national systems.",
    icon: <MdFingerprint />
  },
  {
    title: "Employee Self Service Portals",
    description: "Real-time attendance logs, late-in stats, and remote mobile check-in capabilities for staff.",
    icon: <MdPerson />
  },
  {
    title: "Visitor Management Systems",
    description: "Streamlined registration desks supporting visitor badge generation, host notifications, and watchlists.",
    icon: <MdPerson />
  },
  {
    title: "Cafeteria Smart Management",
    description: "Cashless smart card and biometric dining systems, managing orders, deliveries, and payment balances.",
    icon: <MdRoomService />
  },
];

const products = [
  {
    title: "Fingerprint Biometrics",
    badge: "Most Popular",
    category: "FINGER SCANNER",
    description: "Advanced optical fingerprint scanners providing high-resolution verification under 0.5 seconds.",
    image: "/images/bio/biometric-fingerprint-security-system.jpg",
    specs: ["Scratch-resistant sensor", "1:N Verification Mode", "Store up to 10,000 logs", "USB & TCP/IP interface"]
  },
  {
    title: "Facial Biometrics",
    badge: "Touchless Tech",
    category: " FACE ID",
    description: "Next-generation touchless facial scanners utilizing advanced deep-learning algorithm modules.",
    image: "/images/bio/MicrosoftTeams-image-246-1024x599 (1).jpg",
    specs: ["0.2s Detection Speed", "Dual-camera anti-spoofing", "Massive face template library", "Operates in low-light"]
  },
  {
    title: "COSEC VEGA Door Controller",
    badge: "Premium Build",
    category: "ACCESS PANEL",
    description: "Commercial door controllers engineered for critical locations, integrating cards, PINs, and fingers.",
    image: "/images/bio/14998539935_2c11d8f7f9_c.jpg",
    specs: ["Built-in Wi-Fi & Ethernet", "Auxiliary input/output relay", "IP65 Weatherproof rating", "Tamper detection alert"]
  },
  {
    title: "COSEC VEGA with Face Auth",
    badge: "Next-Gen Elite",
    category: "ULTIMATE SYSTEM",
    description: "State-of-the-art access controller combining face scanning, biometric credentials, and smart fire alarms.",
    image: "/images/bio/63185d29a8932dd05562f96a_Biometric-access-control-system-scene.jpg",
    specs: ["AI Face & Finger Recognition", "Wiegand protocol support", "Door lock relay controller", "VoIP intercom connection"]
  }
];

const BiometricsPage = () => {
  const [scanMode, setScanMode] = React.useState('face'); // 'face' or 'finger'
  const [scanStatus, setScanStatus] = React.useState('scanning'); // 'scanning' or 'verified'

  React.useEffect(() => {
    // Cycle every 4.5 seconds between Face scan and Finger scan modes
    const interval = setInterval(() => {
      setScanMode(prev => prev === 'face' ? 'finger' : 'face');
    }, 4500);

    return () => clearInterval(interval);
  }, []);

  // Separate effect to handle verification delay whenever the mode swaps
  React.useEffect(() => {
    setScanStatus('scanning');
    const timer = setTimeout(() => {
      setScanStatus('verified');
    }, 1800); // 1.8 seconds scanning animation

    return () => clearTimeout(timer);
  }, [scanMode]);

  return (
    <div className="bio-root">
      <section className="bio-hero" style={{ backgroundImage: 'linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.75)), url("/biometric/biometric.png")' }}>
        <div className="bio-hero-inner">
          <div className="bio-hero-content">
            <span className="hero-tagline">
              <MdFingerprint className="tagline-icon" /> SMART IDENTITY & ACCESS
            </span>
            <h1 className="hero-title">
              Widescreen <br />
              <span className="gradient-text">Biometric Integration</span>
            </h1>
            <p className="hero-desc">
              iMatrix Technologies designs, configures, and deploys high-speed fingerprint scanners, contactless AI facial readers, and central time-attendance door controllers. Secure your enterprise assets, streamline HR logs, and connect locks seamlessly.
            </p>
            <div className="hero-ctas">
              <a href="#products" className="btn-primary">
                View Product Line <FaArrowRight className="btn-arrow" />
              </a>
              <a href="#solutions" className="btn-secondary">
                Security Solutions
              </a>
            </div>
          </div>

          <div className="bio-hero-visual">
            <div className={`hero-scanner-card ${scanMode} ${scanStatus}`}>
              <div className="scanner-header">
                <span className="live-badge">{scanMode === 'face' ? 'FACE ID' : 'FINGERPRINT SCAN'}</span>
                <span className="scanner-status">
                  {scanStatus === 'scanning' ? 'SCANNING ACTIVE...' : 'SYSTEM SECURED'}
                </span>
              </div>

              <div className="face-target-wrapper">
                <div className="scanner-target-corner top-left"></div>
                <div className="scanner-target-corner top-right"></div>
                <div className="scanner-target-corner bottom-left"></div>
                <div className="scanner-target-corner bottom-right"></div>
                <div className="scanner-sweep-line"></div>
                
                <div className="avatar-circle">
                  {scanMode === 'face' ? (
                    <MdFace className="scanner-icon face-icon" />
                  ) : (
                    <MdFingerprint className="scanner-icon finger-icon" />
                  )}
                </div>
              </div>

              <div className="scanner-result">
                <div className="pulse-success-indicator"></div>
                <span className="result-text">
                  {scanStatus === 'scanning' 
                    ? (scanMode === 'face' ? 'FACE ID: SCANNING...' : 'FINGERPRINT: ANALYZING...')
                    : (scanMode === 'face' ? 'FACE ID: ACCESS GRANTED' : 'FINGERPRINT: VERIFIED')
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

    {/* ── 2. INTEGRATION ADVANTAGES ── */}
    <section className="bio-assurances-banner">
      <div className="container-wide assurance-grid-bio">
        <div className="assurance-item-bio">
          <div className="assurance-icon-box-bio">
            <FaExchangeAlt />
          </div>
          <div className="assurance-text-bio">
            <h4>Tally & ERP Auto-Sync</h4>
            <p>Connect door attendance directly to payroll software to run instant monthly logs.</p>
          </div>
        </div>

        <div className="assurance-item-bio">
          <div className="assurance-icon-box-bio">
            <FaBuilding />
          </div>
          <div className="assurance-text-bio">
            <h4>Multi-Site Access Loop</h4>
            <p>Administer, add, and revoke biometric access keys remotely across all branches.</p>
          </div>
        </div>

        <div className="assurance-item-bio">
          <div className="assurance-icon-box-bio">
            <FaQrcode />
          </div>
          <div className="assurance-text-bio">
            <h4>RFID & QR Credentialing</h4>
            <p>Support touchless tags, RFID badges, and secure dynamic QR scanner integrations.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ── 3. PRODUCT SHOWCASE GRID (Widescreen 4 columns desktop) ── */}
    <section id="products" className="bio-product-section">
      <div className="container-wide">
        <div className="section-header-centered">
          <span className="sub-title">PRODUCT OFFERINGS</span>
          <h2 className="section-title">Industrial Biometric Systems</h2>
          <p className="section-desc">
            Explore our state-of-the-art biometric door controllers and contactless recognition readers designed for rigorous everyday operations.
          </p>
        </div>

        <div className="bio-product-grid-modern">
          {products.map((product) => (
            <div key={product.title} className="bio-product-card-modern">
              <div className="product-badge-wrap-bio">
                <span className="product-badge-bio">{product.badge}</span>
              </div>

              <div className="bio-product-image-modern">
                <img src={product.image} alt={product.title} />
              </div>

              <div className="bio-product-info-modern">
                <span className="product-category-label">{product.category}</span>
                <h3 className="product-card-title-bio">{product.title}</h3>
                <p className="product-card-desc-bio">{product.description}</p>
                
                <div className="features-divider-bio"></div>

                <ul className="product-specs-list">
                  {product.specs.map((spec, i) => (
                    <li key={i}>
                      <FaCheck className="spec-tick-icon" />
                      <span>{spec}</span>
                    </li>
                  ))}
                </ul>

                <a href="/contact" className="product-inquire-btn">
                  Inquire Now <FaArrowRight className="inquire-arrow" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── 4. SOLUTIONS GRID SECTION (3 columns desktop) ── */}
    <section id="solutions" className="bio-solutions-section">
      <div className="container-wide">
        <div className="section-header-centered">
          <span className="sub-title">SECURITY & ACCESS SOLUTIONS</span>
          <h2 className="section-title">Access Infrastructures</h2>
          <p className="section-desc">
            We provide comprehensive integrations that scale from local business entries to nationwide, cloud-synced security operations.
          </p>
        </div>

        <div className="bio-solutions-grid-modern">
          {solutionsData.map((solution) => (
            <div className="bio-solution-card-modern" key={solution.title}>
              <div className="solution-card-header">
                <div className="bio-solution-icon-modern">{solution.icon}</div>
                <h3 className="solution-card-title">{solution.title}</h3>
              </div>
              <p className="solution-card-desc">{solution.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
)
};

export default BiometricsPage;
